import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import { Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
// markdown plugins
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
// material
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Typography, Divider } from '@mui/material';

// ----------------------------------------------------------------------

// Sticky site header (nav ribbon + navbar + layout offset) height, so anchor
// jumps land below the header instead of underneath it.
const HEADING_SCROLL_OFFSET = { xs: 120, md: 160 };

// react-markdown hands every component a `node` AST reference that must never
// reach the DOM, so strip it before spreading props.
const domProps = (props, keys = []) => omit(props, ['node', ...keys]);

// Visually hidden but still announced by screen readers, for the link text that
// explains a target="_blank" to anyone who cannot see the arrow glyph.
const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

// Single source of truth for heading ids. Exported so a table of contents can
// build anchors that are guaranteed to match the rendered headings.
export function slugifyHeading(children) {
  const text = Array.isArray(children) ? children.join('') : String(children ?? '');

  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ----------------------------------------------------------------------

const MarkdownWrapperStyle = styled('div')(({ theme }) => {
  // There is no dark theme in this app, but `mode` is read rather than assumed
  // so the code survives one being added.
  const isLight = theme.palette.mode !== 'dark';

  const codeSurface = isLight ? theme.palette.grey[900] : theme.palette.grey[900];

  return {
    overflowWrap: 'break-word',
    color: 'text.primary',

    // Headings. lineHeight is left to the theme's per-variant value; the old
    // blanket `1.3` overrode the display sizes and made h1 look cramped.
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: 'text.primary',
      '&:first-of-type': { marginTop: 0 },
    },
    '& h1': { marginTop: theme.spacing(5) },
    '& h2': { marginTop: theme.spacing(5) },
    '& h3': { marginTop: theme.spacing(4) },
    '& h4, & h5, & h6': { marginTop: theme.spacing(3) },

    // Paragraph rhythm. MUI's default `paragraph` margin is overridden in
    // theme/overrides/Typography.js, so the spacing is set explicitly here to
    // keep the vertical rhythm identical regardless of that override.
    '& p': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    '& > p:first-of-type': { marginTop: 0 },

    // Lists. `listStylePosition: outside` with padding keeps the marker in the
    // gutter, and nested lists need their own indent or they inherit the top
    // level's `ol` numbering visually stacked on the same line.
    '& ul, & ol': {
      ...theme.typography.body1,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(5),
      '& li': {
        lineHeight: 1.75,
        '&::marker': { color: theme.palette.primary.main },
      },
      // A list directly inside a list item should sit tight against it.
      '& ul, & ol': {
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
      },
    },
    // A list immediately after a paragraph needs less gap above than the
    // default, otherwise the bullet block reads as detached from its lead-in.
    '& p + ul, & p + ol': { marginTop: theme.spacing(1) },

    // Media. `minWidth: 0` prevents a wide image forcing the flex parent wider
    // than the viewport on mobile.
    '& img': {
      display: 'block',
      maxWidth: '100%',
      height: 'auto',
      margin: theme.spacing(4, 0),
      borderRadius: theme.shape.borderRadius,
    },
    // An image inside a link should not add the standalone figure gap.
    '& a > img': { margin: 0 },

    // Tables. Markdown content here is mostly reference material (fee tables,
    // document checklists) and an unstyled table breaks out of the column and
    // is unreadable on a phone.
    '& table': {
      width: '100%',
      margin: theme.spacing(3, 0),
      borderCollapse: 'collapse',
      fontSize: '0.9375rem',
      display: 'block',
      overflowX: 'auto',
    },
    '& th, & td': {
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& th': {
      fontWeight: 700,
      color: 'text.primary',
      whiteSpace: 'nowrap',
      borderBottomWidth: 2,
    },
    '& tbody tr:last-of-type td': { borderBottom: 'none' },

    // Blockquote. Uses the accent family rather than a serif: the site commits
    // to three families (theme/typography.js), and pulling in a fourth for one
    // component buys a pull-quote look at the cost of an extra webfont.
    '& blockquote': {
      lineHeight: 1.65,
      fontSize: '1.0625rem',
      margin: theme.spacing(4, 0),
      position: 'relative',
      fontFamily: theme.typography.overline.fontFamily,
      fontWeight: 500,
      padding: theme.spacing(2.5, 3, 2.5, 4),
      borderRadius: theme.shape.borderRadius,
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      backgroundColor: isLight ? theme.palette.background.neutral : alpha(theme.palette.common.white, 0.04),
      color: theme.palette.text.secondary,
      '& p, & span': {
        marginTop: 0,
        marginBottom: 0,
        fontSize: 'inherit',
        fontFamily: 'inherit',
      },
      // Nested quotes should lighten rather than stack a second accent bar.
      '& blockquote': {
        margin: theme.spacing(2, 0),
        padding: theme.spacing(2, 2, 2, 3),
        backgroundColor: 'transparent',
        borderLeftColor: alpha(theme.palette.primary.main, 0.4),
      },
    },

    // Code. `pre` scrolls rather than forcing the page wide, and the inline
    // `code` rule is scoped away from blocks so block padding is not doubled.
    '& code': {
      fontSize: '0.875em',
      borderRadius: theme.shape.borderRadius,
      padding: '0.15em 0.4em',
      color: isLight ? theme.palette.warning.darker : theme.palette.warning.lighter,
      backgroundColor: isLight ? alpha(theme.palette.warning.main, 0.12) : alpha(theme.palette.warning.main, 0.16),
      // Long identifiers (endpoints, file names) must break rather than widen
      // the column. `pre-wrap` keeps the author's line breaks.
      overflowWrap: 'anywhere',
    },
    '& pre': {
      margin: theme.spacing(3, 0),
      padding: theme.spacing(2, 2.5),
      overflowX: 'auto',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: codeSurface,
      color: theme.palette.common.white,
      // `block` on a <pre> makes overflow-x work; without it the pre grows to
      // its content and the whole page scrolls sideways.
      maxWidth: '100%',
      '& code': {
        padding: 0,
        borderRadius: 0,
        fontSize: '0.875rem',
        color: 'inherit',
        backgroundColor: 'transparent',
        overflowWrap: 'normal',
        whiteSpace: 'pre',
      },
    },
    // rehype-highlight emits hljs-* classes but no theme is loaded, so tokens
    // would otherwise inherit the block's flat colour and the highlight would be
    // invisible work. A minimal inline palette keeps the dependency-free theme.
    '& pre code.hljs': { padding: 0, backgroundColor: 'transparent' },
    '& .hljs-comment, & .hljs-quote': { color: '#8b949e', fontStyle: 'italic' },
    '& .hljs-keyword, & .hljs-selector-tag, & .hljs-literal, & .hljs-doctag': { color: '#ff7b72' },
    '& .hljs-string, & .hljs-regexp, & .hljs-addition': { color: '#a5d6ff' },
    '& .hljs-number, & .hljs-symbol, & .hljs-bullet': { color: '#79c0ff' },
    '& .hljs-title, & .hljs-title.function_, & .hljs-section, & .hljs-name': { color: '#d2a8ff' },
    '& .hljs-attr, & .hljs-attribute, & .hljs-variable, & .hljs-template-variable': { color: '#ffa657' },
    '& .hljs-built_in, & .hljs-class .hljs-title, & .hljs-type': { color: '#7ee787' },
    '& .hljs-meta, & .hljs-meta .hljs-keyword': { color: '#79c0ff' },
    '& .hljs-deletion': { color: '#ffa198' },
    '& .hljs-emphasis': { fontStyle: 'italic' },
    '& .hljs-strong': { fontWeight: 700 },

    // Inline emphasis. `em` was previously dimmed to text.secondary, which read
    // as "less important" — italics are emphasis, not de-emphasis.
    '& em': { fontStyle: 'italic' },
    '& strong': { fontWeight: 700, color: 'text.primary' },

    // Keyboard focus on the wrapper should be visible when a link inside is
    // focused via `outline`, but never on mouse click.
    '& a:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  };
});

const HeadingAnchorStyle = styled('a')(({ theme }) => ({
  flexShrink: 0,
  fontSize: '0.85em',
  lineHeight: 1,
  textDecoration: 'none',
  color: theme.palette.text.disabled,
  opacity: 0,
  transition: 'opacity 0.2s ease, color 0.2s ease',
  '&:focus-visible': { opacity: 1, color: theme.palette.primary.main },
  // Anchor links are for mouse users; the TOC is the keyboard path to sections.
  '@media (hover: none)': { display: 'none' },
}));

// ----------------------------------------------------------------------

// Headings get a stable id plus a hover anchor so any section can be linked
// to or shared.
function createHeading(variant) {
  function MarkdownHeading(props) {
    const { children, id } = props;
    const headingId = id || slugifyHeading(children);

    // A heading that is only punctuation or an emoji slugifies to an empty
    // string, which would emit `id=""` and make the anchor link point at the
    // page top. Skip the anchor rather than emit a broken one.
    const hasAnchor = Boolean(headingId);

    return (
      <Box
        className="markdown-heading"
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          scrollMarginTop: HEADING_SCROLL_OFFSET,
          '&:hover .markdown-heading-anchor': { opacity: 1 },
        }}
      >
        <Typography
          {...domProps(props, ['children', 'id'])}
          id={headingId || undefined}
          variant={variant}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {children}
        </Typography>

        {hasAnchor && (
          <HeadingAnchorStyle
            className="markdown-heading-anchor"
            href={`#${headingId}`}
            tabIndex={-1}
            aria-hidden="true"
          >
            #
          </HeadingAnchorStyle>
        )}
      </Box>
    );
  }

  MarkdownHeading.propTypes = {
    children: PropTypes.node,
    id: PropTypes.string,
  };

  return MarkdownHeading;
}

// ----------------------------------------------------------------------

function MarkdownParagraph(props) {
  return <Typography {...domProps(props)} variant="body1" component="p" />;
}

MarkdownParagraph.propTypes = {
  children: PropTypes.node,
};

function MarkdownImage(props) {
  return (
    <Box
      {...domProps(props, ['alt'])}
      component="img"
      alt={props.alt || ''}
      loading="lazy"
      decoding="async"
    />
  );
}

MarkdownImage.propTypes = {
  alt: PropTypes.string,
};

function MarkdownStrong(props) {
  return <Box {...domProps(props)} component="strong" sx={{ fontWeight: 700, color: 'text.primary' }} />;
}

MarkdownStrong.propTypes = {
  children: PropTypes.node,
};

function MarkdownEmphasis(props) {
  return <Box {...domProps(props)} component="em" />;
}

MarkdownEmphasis.propTypes = {
  children: PropTypes.node,
};

function MarkdownDelete(props) {
  return <Box {...domProps(props)} component="del" sx={{ color: 'text.secondary' }} />;
}

MarkdownDelete.propTypes = {
  children: PropTypes.node,
};

function MarkdownDivider(props) {
  return <Divider {...domProps(props)} sx={{ my: 4 }} />;
}

// ----------------------------------------------------------------------

LinkTo.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
};

// Internal paths route client-side; in-page anchors and external links stay
// plain links so the browser handles them natively.
function LinkTo({ href, children }) {
  const isInternal = href.startsWith('/') && !href.startsWith('//');

  if (isInternal) {
    return (
      <Link component={RouterLink} to={href} underline="hover">
        {children}
      </Link>
    );
  }

  if (href.startsWith('#') || /^(mailto|tel):/.test(href)) {
    return (
      <Link href={href} underline="hover">
        {children}
      </Link>
    );
  }

  // `noopener` severs `window.opener` for target="_blank"; `noreferrer` also
  // withholds the referrer. The external icon is `aria-hidden` with the meaning
  // carried in the accessible name, so screen readers don't announce "link,
  // opens in new tab, graphic".
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer nofollow" underline="hover">
      {children}
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          display: 'inline-block',
          ml: 0.5,
          fontSize: '0.85em',
          lineHeight: 1,
          transform: 'translateY(-1px)',
        }}
      >
        ↗
      </Box>
      <Box component="span" sx={visuallyHiddenSx}>
        (opens in a new tab)
      </Box>
    </Link>
  );
}

Markdown.propTypes = {
  components: PropTypes.object,
};

export default function Markdown({ components, ...other }) {
  const baseComponents = {
    h1: createHeading('h1'),
    h2: createHeading('h2'),
    h3: createHeading('h3'),
    h4: createHeading('h4'),
    h5: createHeading('h5'),
    h6: createHeading('h6'),
    p: MarkdownParagraph,
    img: MarkdownImage,
    strong: MarkdownStrong,
    em: MarkdownEmphasis,
    del: MarkdownDelete,
    hr: MarkdownDivider,
    a: LinkTo,
  };

  return (
    <MarkdownWrapperStyle>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, rehypeHighlight]}
        // Caller-supplied components win, so a page can override one element
        // without giving up the rest.
        components={{ ...baseComponents, ...components }}
        {...other}
      />
    </MarkdownWrapperStyle>
  );
}
